import { selectSharedViewDocument } from "./sharedViewSelectors";
import { useEffect, useState, type ReactNode } from "react";
import { Alert, Button, Center, Loader, Stack } from "@mantine/core";
import { Link, useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { useInternalMsalAccount } from "../../hooks/useInternalMsalAccount";
import { ModuleRoute } from "../../Modules/ModuleRoute";
import { selectAvailableModulesForCurrentUserInCurrentProject, selectIsCurrentUserAccessReady, selectVisibleModuleKeysForCurrentProject } from "../../projectSelectors";
import { setActiveSharedViewInRedux } from "../../ViewerRedux/viewerSlice";
import { applySharedViewStateThunk } from "../viewThunks";
import { useGetSharedViewQuery } from "./sharedViewsApi";

/** Resolve state-only links before ModuleRoute requires a module in the URL. */
export function SharedViewRoute({ projectNo, children }: { projectNo: number; children: ReactNode }) {
  const [params] = useSearchParams();
  const stateId = params.get("state");
  const validId = stateId != null && /^[0-9a-fA-F]{24}$/.test(stateId);
  const dispatch = useAppDispatch();
  const active = useAppSelector(state => state.viewer.activeSharedView);
  const visible = useAppSelector(selectVisibleModuleKeysForCurrentProject);
  const available = useAppSelector(selectAvailableModulesForCurrentUserInCurrentProject);
  const ready = useAppSelector(selectIsCurrentUserAccessReady);
  const internal = useInternalMsalAccount();
  const alreadyApplied = stateId != null && active?.id === stateId;
  const { currentData: response, isError, refetch } = useGetSharedViewQuery(
    { projectNo, viewId: stateId ?? "" },
    { skip: !validId || alreadyApplied || (!ready && !internal) },
  );
  const saved = selectSharedViewDocument(response);
  const module = alreadyApplied ? active.state.context.module : saved?.context.module;
  const allowed = module != null && visible.includes(module) && (internal || available.includes(module));
  const [restoreError, setRestoreError] = useState<{ id: string; message: string } | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => () => { dispatch(setActiveSharedViewInRedux(null)); }, [dispatch]);

  useEffect(() => {
    if (!stateId || !validId || !saved || saved._id !== stateId.toLowerCase() || alreadyApplied || !allowed || (!ready && !internal)) return;
    const controller = new AbortController();
    setRestoreError(null);
    dispatch(applySharedViewStateThunk({ view: saved.view, context: saved.context }, controller.signal)).then(() => {
      if (!controller.signal.aborted) dispatch(setActiveSharedViewInRedux({ id: stateId, state: { view: saved.view, context: saved.context } }));
    }).catch((error: unknown) => {
      if (!controller.signal.aborted) console.error("Failed to restore shared view", error);
      if (!controller.signal.aborted) setRestoreError({ id: stateId, message: "Kunne ikke gjenopprette visningen. Etasjen kan ha blitt endret eller fjernet." });
    });
    return () => controller.abort();
  }, [stateId, validId, saved, alreadyApplied, allowed, ready, internal, dispatch, attempt]);

  if (stateId != null) {
    const message = !validId ? "Lenken til visningen er ugyldig."
      : (isError || (response && !saved)) ? "Kunne ikke hente visningen. Lenken finnes ikke, du mangler tilgang, eller tjenesten er utilgjengelig."
      : (ready || internal) && module && !allowed ? "Du har ikke tilgang til modulen i denne visningen."
      : restoreError?.id === stateId ? restoreError.message : null;
    if (message) return <Center p="xl"><Stack maw={520}>
      <Alert color="red" title="Kunne ikke åpne visningen" role="alert">{message}</Alert>
      {(isError || restoreError?.id === stateId) && <Button onClick={() => { setRestoreError(null); if (isError) void refetch(); else setAttempt(value => value + 1); }}>Prøv igjen</Button>}
      <Button component={Link} to={`/${projectNo}/modules`} variant="neutral">Til modulvalg</Button>
    </Stack></Center>;
    if (!alreadyApplied) return <Center p="xl"><Stack align="center"><Loader size="sm" /><span role="status">Åpner delt visning…</span></Stack></Center>;
  }

  return <ModuleRoute projectNo={projectNo} page="design" moduleOverride={stateId ? module : undefined}>{children}</ModuleRoute>;
}
